/**
 * Calendar API Client
 * Epic 57: Business Hours & Calendar Management
 *
 * 47Y route + response-shape fix: a CalendarController minden végpontja `{ data: ... }`
 * borítékban válaszol (a rPI ERP API-konvenció, lásd CLAUDE.md "API Konvenciók"), de ez a
 * kliens korábban a nyers választ típusozta/adta vissza borítékolás nélkül — a CalendarModule
 * sosem volt bekötve az AppModule-ba, így ez a hiba sosem futott le éles hívásnál. A
 * modul-wiring-fix (app.module.ts) után ez a réteg is javítva lett, különben a 404 helyett
 * csendes hibás-adat (undefined mezők / `.map is not a function`) jelentkezett volna.
 */

import { api } from './client';

// ============================================
// TYPES (matching backend schema)
// ============================================

export interface BusinessHoursDay {
  id: string;
  dayOfWeek: number; // 0=Monday...6=Sunday
  openTime: string; // "08:00"
  closeTime: string; // "17:00"
  isOpen: boolean;
}

export interface Holiday {
  id: string;
  date: string; // ISO date
  name: string;
  isRecurring: boolean;
  isNational: boolean;
}

export interface BusinessHoursOverride {
  id: string;
  date: string;
  openTime: string | null;
  closeTime: string | null;
  isOpen: boolean;
  reason: string | null;
}

export interface BusinessDayInfo {
  date: string;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
  isHoliday: boolean;
  isOverride: boolean;
  reason?: string;
}

export interface CreateHolidayInput {
  date: string;
  name: string;
  isRecurring?: boolean;
  isNational?: boolean;
}

export interface UpdateHolidayInput {
  date?: string;
  name?: string;
  isRecurring?: boolean;
  isNational?: boolean;
}

export interface UpdateBusinessHoursDayInput {
  openTime?: string;
  closeTime?: string;
  isOpen?: boolean;
}

export interface CreateOverrideInput {
  date: string;
  openTime?: string | null;
  closeTime?: string | null;
  isOpen: boolean;
  reason?: string | null;
}

export interface UpdateOverrideInput {
  openTime?: string | null;
  closeTime?: string | null;
  isOpen?: boolean;
  reason?: string | null;
}

/** Generic `{ data: T }` envelope — the rPI ERP API convention (CLAUDE.md). */
interface DataEnvelope<T> {
  data: T;
}

// ============================================
// API FUNCTIONS — Business Hours
// ============================================

/**
 * Get all business hours (7 days)
 */
export async function fetchBusinessHours(): Promise<BusinessHoursDay[]> {
  const response = await api.get<DataEnvelope<BusinessHoursDay[]>>('/calendar/business-hours');
  return response.data;
}

/**
 * Update a single day's business hours
 */
export async function updateBusinessHoursDay(
  day: number,
  data: UpdateBusinessHoursDayInput
): Promise<BusinessHoursDay> {
  // 47Y route-fix: a BE csak PUT-ot regisztrál erre a route-ra (CalendarController
  // @Put('business-hours/:day')) — a korábbi PATCH sosem talált volna route-ot (404),
  // ez csak a modul-wiring-fix után derült volna ki éles-hívásnál.
  const response = await api.put<DataEnvelope<BusinessHoursDay>>(
    `/calendar/business-hours/${day}`,
    data
  );
  return response.data;
}

/**
 * Reset business hours to defaults
 */
export async function resetBusinessHours(): Promise<BusinessHoursDay[]> {
  const response = await api.post<DataEnvelope<BusinessHoursDay[]>>(
    '/calendar/business-hours/reset'
  );
  return response.data;
}

// ============================================
// API FUNCTIONS — Holidays
// ============================================

/**
 * Get holidays, optionally filtered by year
 */
export async function fetchHolidays(year?: number): Promise<Holiday[]> {
  const params = new URLSearchParams();
  if (year !== undefined) params.append('year', year.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/calendar/holidays?${queryString}` : '/calendar/holidays';

  const response = await api.get<DataEnvelope<Holiday[]>>(endpoint);
  return response.data;
}

/**
 * Create a new holiday
 */
export async function createHoliday(data: CreateHolidayInput): Promise<Holiday> {
  const response = await api.post<DataEnvelope<Holiday>>('/calendar/holidays', data);
  return response.data;
}

/**
 * Update an existing holiday
 */
export async function updateHoliday(id: string, data: UpdateHolidayInput): Promise<Holiday> {
  const response = await api.patch<DataEnvelope<Holiday>>(`/calendar/holidays/${id}`, data);
  return response.data;
}

/**
 * Delete a holiday
 */
export async function deleteHoliday(id: string): Promise<void> {
  return api.delete<void>(`/calendar/holidays/${id}`);
}

/**
 * Seed national holidays for a given year
 */
export async function seedNationalHolidays(year: number): Promise<{ seeded: number }> {
  // 47Y route-fix: a BE route `holidays/seed-national` (CalendarController), a korábbi
  // `/calendar/holidays/seed` sosem létezett → 404. A válasz-boríték mezőneve `created`
  // (nem `seeded`) — itt map-eljük a FE hívó-konvencióra.
  const response = await api.post<
    DataEnvelope<{ year: number; totalProcessed: number; created: number; skipped: number }>
  >('/calendar/holidays/seed-national', { year });
  return { seeded: response.data.created };
}

// ============================================
// API FUNCTIONS — Overrides
// ============================================

/**
 * Get business hours overrides, optionally filtered by date range
 */
export async function fetchOverrides(from?: string, to?: string): Promise<BusinessHoursOverride[]> {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  const queryString = params.toString();
  const endpoint = queryString ? `/calendar/overrides?${queryString}` : '/calendar/overrides';

  const response = await api.get<DataEnvelope<BusinessHoursOverride[]>>(endpoint);
  return response.data;
}

/**
 * Create a new override
 */
export async function createOverride(data: CreateOverrideInput): Promise<BusinessHoursOverride> {
  const response = await api.post<DataEnvelope<BusinessHoursOverride>>('/calendar/overrides', data);
  return response.data;
}

/**
 * Update an existing override
 */
export async function updateOverride(
  id: string,
  data: UpdateOverrideInput
): Promise<BusinessHoursOverride> {
  const response = await api.patch<DataEnvelope<BusinessHoursOverride>>(
    `/calendar/overrides/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete an override
 */
export async function deleteOverride(id: string): Promise<void> {
  return api.delete<void>(`/calendar/overrides/${id}`);
}

// ============================================
// API FUNCTIONS — Calendar Queries
// (Megjegyzés: ezeket a függvényeket jelenleg egyik FE-oldal sem hívja — az útvonal +
// boríték-javítás itt is elvégezve konzisztencia-okból, de élő 404-bug ezekhez ma nem
// tartozik.)
// ============================================

/**
 * Get business day info for a specific date
 */
export async function fetchDayInfo(date: string): Promise<BusinessDayInfo> {
  const response = await api.get<DataEnvelope<BusinessDayInfo>>(`/calendar/day/${date}`);
  return response.data;
}

/**
 * Get calendar data for an entire month
 */
export async function fetchMonthCalendar(year: number, month: number): Promise<BusinessDayInfo[]> {
  const response = await api.get<
    DataEnvelope<{ year: number; month: number; daysInMonth: number; days: BusinessDayInfo[] }>
  >(`/calendar/month/${year}/${month}`);
  return response.data.days;
}

/**
 * Get the count of business days between two dates
 */
export async function fetchBusinessDaysCount(from: string, to: string): Promise<{ count: number }> {
  const params = new URLSearchParams();
  params.append('from', from);
  params.append('to', to);

  const response = await api.get<DataEnvelope<{ businessDays: number; calendarDays: number }>>(
    `/calendar/business-days?${params.toString()}`
  );
  return { count: response.data.businessDays };
}

/**
 * Get the next available business day after a given date
 */
export async function fetchNextAvailable(after: string): Promise<{ nextAvailable: string }> {
  const params = new URLSearchParams();
  params.append('after', after);

  const response = await api.get<DataEnvelope<{ nextAvailable: string }>>(
    `/calendar/next-available?${params.toString()}`
  );
  return response.data;
}
